
import { computed, onMounted, ref, watch } from 'vue';
import { useStoreInNodes } from '@/composables/useStoreInNodes';

import {
  GetCurrentConversationsList,
  GetCurrentConversation,
  GetCurrentAmp,
  GetConversationsList,
} from '@/constants/getters';
import { FetchEDetailAmps } from '@/constants/actions';
import CardLabel from '@/components/common/CardLabel';
import ConversationThumbnail from '@/components/common/ConversationThumbnail';

export default {
  components: {
    CardLabel,
    ConversationThumbnail,
  },

  props: {
    disableCurrentConversation: { type: Boolean, default: true },
    disableCurrentAmp: { type: Boolean, default: true },
    onSelectConversation: { type: Function, default: () => {} },
    selectedAmpId: { type: String, default: '' },
    selectedConversationId: { type: String, default: '' },
    backgroundColor: { type: String, default: '' },
    searchThroughEDetails: { type: Boolean, default: false },
  },

  setup(props) {
    const { store } = useStoreInNodes();

    const searchValue = ref('');
    const filteredConversations = ref([]);
    const searchEDetailsValue = ref('');
    const filteredEDetails = ref([]);
    const showAmpsList = ref(false);
    const selectedAmp = ref({});
    const ampsList = ref([]);
    const loadingAmps = ref(false);

    const conversationStyle = computed(() => ({ 'bg-color': props.backgroundColor }));
    const currentAmp = computed(() => store.value.getters[`ampStore/${GetCurrentAmp}`]);
    const currentConversation = computed(() => store.value.getters[`builderStore/${GetCurrentConversation}`]);
    const currentConversationsList = computed(() => store.value.getters[`builderStore/${GetCurrentConversationsList}`]);

    const conversationsList = computed(() =>
      selectedAmp.value?.data?.conversations
        ? getConversationsList(selectedAmp.value.data.conversations)
        : currentConversationsList.value
    );

    const getConversationsList = (conversations) =>
      store.value.getters[`builderStore/${GetConversationsList}`](conversations);

    watch(searchValue, (value, oldValue) => {
      const searchedValue = parseSearchValue(value);
      const oldValueTrimmed = parseSearchValue(oldValue);

      if (searchedValue !== oldValueTrimmed) {
        filteredConversations.value = searchedValue
          ? filterList(conversationsList.value, searchedValue)
          : conversationsList.value;
      }
    });

    watch(searchEDetailsValue, (value, oldValue) => {
      const searchedValue = parseSearchValue(value);
      const oldValueTrimmed = parseSearchValue(oldValue);

      if (searchedValue !== oldValueTrimmed) {
        filteredEDetails.value = searchedValue ? filterList(ampsList.value, searchedValue) : ampsList.value;
      }
    });

    watch(
      conversationsList,
      (newValue) => {
        const searchedValue = parseSearchValue(searchValue.value);

        filteredConversations.value = searchedValue ? filterList(newValue, searchedValue) : newValue;
      },
      { deep: true }
    );

    watch(
      ampsList,
      (newValue) => {
        const searchedValue = parseSearchValue(searchValue.value);

        filteredEDetails.value = searchedValue ? filterList(newValue, searchedValue) : newValue;
      },
      { deep: true }
    );

    onMounted(async () => {
      const searchedValue = parseSearchValue(searchValue.value);
      showAmpsList.value = props.searchThroughEDetails;

      if (props.searchThroughEDetails) {
        loadingAmps.value = true;
        ampsList.value = await store.value.dispatch(`ampStore/${FetchEDetailAmps}`);
        loadingAmps.value = false;

        filteredConversations.value = searchedValue
          ? filterList(conversationsList.value, searchedValue)
          : conversationsList.value;

        filteredEDetails.value = searchedValue ? filterList(ampsList.value, searchedValue) : ampsList.value;
      }

      selectedAmp.value = ampsList.value.find((amp) => amp.id === props.selectedAmpId) || currentAmp.value;
    });

    const filterList = (items, value) => [...items.filter((item) => item.name?.toLowerCase().includes(value))];

    const parseSearchValue = (value) => value?.toLowerCase().trim() || '';

    const onSelectEDetail = (amp) => {
      if (currentAmp.value.id === amp.id && props.disableCurrentAmp) {
        return;
      }

      selectedAmp.value = amp;
      showAmpsList.value = false;
    };

    const onBackToAmps = () => (showAmpsList.value = true);

    const showAmpThumbnail = (amp) => amp.ampType !== 'website';

    return {
      conversationStyle,
      currentAmp,
      currentConversation,
      filteredConversations,
      filteredEDetails,
      loadingAmps,
      searchEDetailsValue,
      searchValue,
      selectedAmp,
      showAmpsList,
      onSelectEDetail,
      onBackToAmps,
      showAmpThumbnail,
    };
  },
};
